import React, {useEffect, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {CancelToken, isCancel} from 'axios';
import AppText from '../../../components/AppText';
import MessageFeedResolvedSvg from '../../../components/svgs/MessageFeedResolvedSvg';
import MessageFeedUnresolvedSvg from '../../../components/svgs/MessageFeedUnresolvedSvg';
import MessagesService from '../../../services/MessagesService';
import {useDarkMode} from '../DarkModeContext';
import {getThemeTransitionStyle} from '../theme';

const STATUS_REFRESH_INTERVAL_MS = 15000;

// sharedStatus is {organizationId, count} from the Inbox's per-user count poll. While
// useSharedStatus is set it keeps the banner current, so the banner fetches the total once
// for an immediate value instead of polling it as well.
export default function UnresolvedStatusBanner({organizationId, sharedStatus = null, useSharedStatus = false}) {
    const {theme} = useDarkMode();
    const [status, setStatus] = useState(null);
    const [statusMessage, setStatusMessage] = useState('Checking status…');

    useEffect(() => {
        setStatus(null);
        setStatusMessage('Checking status…');
    }, [organizationId]);

    useEffect(() => {
        let active = true;
        let timeout;
        const request = CancelToken.source();

        const refresh = async () => {
            try {
                // Organization-wide total: deliberately independent of list filters and pagination.
                const value = await MessagesService.getMessageFeedsTotals(organizationId, request.token);
                let count = value == null ? null : Number(value);
                if (!Number.isFinite(count)) count = null;
                if (active) {
                    setStatus(count === null ? null : {organizationId, count});
                    setStatusMessage(count === null ? 'Unable to load unresolved status.' : null);
                }
            } catch (error) {
                if (active && !isCancel(error)) {
                    setStatus(null);
                    setStatusMessage('Unable to load unresolved status.');
                }
            } finally {
                if (active && !useSharedStatus) timeout = setTimeout(refresh, STATUS_REFRESH_INTERVAL_MS);
            }
        };

        if (organizationId) refresh();
        return () => {
            active = false;
            clearTimeout(timeout);
            request.cancel('Unresolved status changed organization or unmounted');
        };
    }, [organizationId, useSharedStatus]);

    useEffect(() => {
        if (useSharedStatus && sharedStatus && sharedStatus.organizationId == organizationId) {
            setStatus({organizationId, count: sharedStatus.count});
            setStatusMessage(null);
        }
        // Only a newly fetched sharedStatus updates the banner. Re-entering shared mode must not
        // reapply a result from before the filter changed; the fetch above covers that case.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sharedStatus]);

    const current = status && status.organizationId === organizationId ? status : null;
    const hasCount = current !== null;
    const unresolved = hasCount && current.count > 0;
    const backgroundColor = hasCount ? (unresolved ? theme.unresolvedBannerBg : theme.resolvedBannerBg) : theme.statusBannerNeutralBg;

    return (
        <View
            style={[styles.banner, {backgroundColor}, getThemeTransitionStyle()]}
        >
            {hasCount && (unresolved
                ? <MessageFeedUnresolvedSvg width={16} height={17} color={unresolved ? theme.unresolvedBannerText : theme.resolvedBannerText} style={styles.icon} />
                : <MessageFeedResolvedSvg width={16} height={17} color={unresolved ? theme.unresolvedBannerText : theme.resolvedBannerText} style={styles.icon} />
            )}
            <AppText
                style={[styles.label, unresolved ? {color: theme.unresolvedBannerText} : {color: theme.resolvedBannerText}, !hasCount && {color: theme.statusBannerNeutralText}, getThemeTransitionStyle()]}
            >
                {statusMessage || (unresolved ? 'Unresolved' : 'All resolved')}
            </AppText>
            {unresolved &&
             <AppText 
                style={[styles.count, {backgroundColor: theme.unresolvedBadgeBg, color: theme.unresolvedBadgeText}, getThemeTransitionStyle()]}
             >
                {current.count}
            </AppText>
            }
        </View>
    );
}

const styles = StyleSheet.create({
    banner: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        paddingVertical: 12,
        paddingHorizontal: 16,
        marginBottom: 8,
        flexShrink: 0,
    },
    icon: {marginRight: 8},
    label: {fontSize: 14, fontWeight: '600'},
    count: {
        marginLeft: 8,
        fontSize: 12,
        fontWeight: '600',
        paddingVertical: 1,
        paddingHorizontal: 9,
        borderRadius: 999,
        overflow: 'hidden',
    },
});
